import Dexie, { type EntityTable } from "dexie";
import type {
  DailyFocus,
  MetaItem,
  OutboxItem,
  WorkRecord,
  WorkbenchBackup
} from "../types";
import { nowIso } from "./time";

class WorkbenchDatabase extends Dexie {
  records!: EntityTable<WorkRecord, "id">;
  outbox!: EntityTable<OutboxItem, "recordId">;
  meta!: EntityTable<MetaItem, "key">;

  constructor() {
    super("founder-workbench");
    this.version(1).stores({
      records: "id, kind, status, dueAt, reviewAt, updatedAt, deletedAt",
      outbox: "recordId, opId, createdAt",
      meta: "key"
    });
  }
}

export const db = new WorkbenchDatabase();

export function makeId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function saveRecord<T extends WorkRecord>(record: T): Promise<T> {
  const saved = await db.transaction("rw", db.records, db.outbox, async () => {
    const previous = await db.records.get(record.id);
    const existingOutbox = await db.outbox.get(record.id);
    const next = {
      ...record,
      revision: previous?.revision ?? record.revision ?? 0,
      updatedAt: nowIso()
    } as T;
    const baseRevision =
      existingOutbox?.baseRevision ?? previous?.revision ?? record.revision ?? 0;
    const outbox: OutboxItem = {
      recordId: next.id,
      opId: existingOutbox?.opId ?? makeId(),
      baseRevision,
      record: next,
      createdAt: existingOutbox?.createdAt ?? nowIso()
    };
    await db.records.put(next);
    await db.outbox.put(outbox);
    return next;
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("workbench:local-change"));
  }
  return saved;
}

export async function softDeleteRecord(id: string): Promise<void> {
  await db.transaction("rw", db.records, db.outbox, async () => {
    const record = await db.records.get(id);
    if (!record) return;
    const existingOutbox = await db.outbox.get(id);
    const deleted = { ...record, deletedAt: nowIso(), updatedAt: nowIso() };
    await db.records.put(deleted);
    await db.outbox.put({
      recordId: id,
      opId: existingOutbox?.opId ?? makeId(),
      baseRevision: existingOutbox?.baseRevision ?? record.revision,
      record: deleted,
      createdAt: existingOutbox?.createdAt ?? nowIso()
    });
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("workbench:local-change"));
  }
}

export async function getTodayFocus(date: string): Promise<DailyFocus | undefined> {
  const records = await db.records
    .where("kind")
    .equals("dailyFocus")
    .filter((record) => record.kind === "dailyFocus" && record.date === date && !record.deletedAt)
    .toArray();
  return records[0] as DailyFocus | undefined;
}

export async function createTodayFocus(date: string): Promise<DailyFocus> {
  const existing = await getTodayFocus(date);
  if (existing) return existing;
  const timestamp = nowIso();
  const focus: DailyFocus = {
    id: makeId(),
    kind: "dailyFocus",
    title: `${date} 今日聚焦`,
    body: "",
    createdAt: timestamp,
    updatedAt: timestamp,
    revision: 0,
    date,
    taskIds: [],
    closeNote: ""
  };
  return saveRecord(focus);
}

export async function getMeta(key: string): Promise<string | undefined> {
  return (await db.meta.get(key))?.value;
}

export async function setMeta(key: string, value: string): Promise<void> {
  await db.meta.put({ key, value });
}

export async function clearAllLocalData(): Promise<void> {
  await db.transaction("rw", db.records, db.outbox, db.meta, async () => {
    await db.records.clear();
    await db.outbox.clear();
    await db.meta.clear();
  });
}

export async function createBackup(): Promise<WorkbenchBackup> {
  const records = await db.records.toArray();
  return {
    schemaVersion: 1,
    exportedAt: nowIso(),
    records
  };
}

export async function restoreBackup(backup: WorkbenchBackup): Promise<number> {
  if (backup.schemaVersion !== 1 || !Array.isArray(backup.records)) {
    throw new Error("备份文件格式不正确");
  }
  await db.transaction("rw", db.records, db.outbox, async () => {
    await db.records.clear();
    await db.outbox.clear();
    await db.records.bulkPut(backup.records);
    await db.outbox.bulkPut(
      backup.records.map((record) => ({
        recordId: record.id,
        opId: makeId(),
        baseRevision: record.revision,
        record,
        createdAt: nowIso()
      }))
    );
  });
  return backup.records.length;
}

export async function applyAcceptedRecord(
  accepted: WorkRecord,
  sentUpdatedAt: string
): Promise<void> {
  await db.transaction("rw", db.records, db.outbox, async () => {
    const currentOutbox = await db.outbox.get(accepted.id);
    const currentLocal = await db.records.get(accepted.id);
    if (currentOutbox && currentOutbox.record.updatedAt !== sentUpdatedAt) {
      await db.outbox.put({
        ...currentOutbox,
        baseRevision: accepted.revision
      });
      if (currentLocal) {
        await db.records.put({ ...currentLocal, revision: accepted.revision });
      }
      return;
    }
    await db.outbox.delete(accepted.id);
    await db.records.put(accepted);
  });
}

export async function applyRemoteRecord(record: WorkRecord): Promise<void> {
  await db.transaction("rw", db.records, db.outbox, async () => {
    const pending = await db.outbox.get(record.id);
    if (pending) return;
    await db.records.put(record);
  });
}

export async function createConflictCopy(
  record: WorkRecord,
  conflictMessage = "同步冲突副本"
): Promise<WorkRecord> {
  const timestamp = nowIso();
  const copy = {
    ...record,
    id: makeId(),
    title: `${record.title || "未命名记录"}（${conflictMessage}）`,
    createdAt: timestamp,
    updatedAt: timestamp,
    revision: 0,
    deletedAt: undefined,
    sourceIds: [...(record.sourceIds ?? []), record.id]
  } as WorkRecord;
  await saveRecord(copy);
  return copy;
}

export async function preserveLocalAndApplyConflict(remote: WorkRecord): Promise<void> {
  await db.transaction("rw", db.records, db.outbox, async () => {
    const local = await db.records.get(remote.id);
    const pending = await db.outbox.get(remote.id);
    if (local && pending) {
      const timestamp = nowIso();
      const copy = {
        ...local,
        id: makeId(),
        title: `${local.title || "未命名记录"}（同步冲突副本）`,
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 0,
        deletedAt: undefined,
        sourceIds: [...(local.sourceIds ?? []), local.id]
      } as WorkRecord;
      await db.records.put(copy);
      await db.outbox.put({
        recordId: copy.id,
        opId: makeId(),
        baseRevision: 0,
        record: copy,
        createdAt: timestamp
      });
    }
    await db.outbox.delete(remote.id);
    await db.records.put(remote);
  });
}
